import React, { useLayoutEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppContext } from "./AppContext";
import EventNotFound from "./Pages/NonAppPages/EventNotFound/EventNotFound";
import {
  activateEventScope,
  clearEventScope,
  getEventScope,
  newGuestId,
  readStoredGuestId,
  resetEventFlowStorage,
  setActiveGuestId,
  storeGuestIdentity,
  validateEventName,
} from "./eventIdentity";

// The gate writes the canonical (already route-decoded) name, so it is
// compared as stored: decoding it again would turn an accepted literal name
// such as "swingies_A%20B_d" into a different event after a reload.
const readStoredEventName = () => sessionStorage.getItem("event_name") || null;

// The single place that turns the URL's event segment into the guest event
// identity. Children (EventWrapper -> pages) are withheld until the validated
// URL name, the AppContext event name and a fresh guest scope all line up, so
// deep entries (face/body gallery, which bypass Home) and A -> B route-param
// switches never render with the previous event's data or guest id.
const EventRouteGate = ({ children }) => {
  const { eventName: routeParam } = useParams();
  const name = validateEventName(routeParam);
  const {
    eventName: contextEventName,
    userId,
    setEventName,
    setUserId,
    setImages,
    setSelectedImages,
    setTotalValue,
  } = useAppContext();
  const [aligned, setAligned] = useState(null); // { scope } once synchronized

  useLayoutEffect(() => {
    if (name === null) {
      clearEventScope();
      setAligned(null);
      return undefined;
    }

    const previous = getEventScope()?.name ?? readStoredEventName();
    // Unknown previous identity is treated like a switch: nothing in storage
    // can be proven to belong to this event, so it must not be inherited.
    const switchingEvent = previous !== name;
    // Storage is the carrier across a reload (the register is empty here: the effect's own cleanup, or the
    // invalid-route branch, cleared it); a missing key means a fresh guest, as before.
    let guestId = readStoredGuestId();

    if (switchingEvent) {
      // Drop event A's guest flow BEFORE exposing event B.
      resetEventFlowStorage(name);
      setImages([]);
      setSelectedImages([]);
      setTotalValue(0);
      setUserId(null);
      setActiveGuestId(null);
      guestId = null;
    }
    if (!guestId) {
      guestId = newGuestId();
    }
    // The register and the context are set together: the claim client reads the register, the
    // approve reads the context, and both must name the same guest (2026-10-18 guest_mismatch incident).
    storeGuestIdentity(name, guestId);
    setActiveGuestId(guestId);
    setUserId(guestId);
    setEventName(name);

    const scope = activateEventScope(name);
    setAligned({ scope });
    return () => {
      clearEventScope(scope);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  if (name === null) {
    return <EventNotFound />;
  }

  const currentScope = getEventScope();
  const ready =
    aligned !== null &&
    currentScope === aligned.scope &&
    aligned.scope.name === name &&
    contextEventName === name &&
    Boolean(userId);
  if (!ready) {
    return null;
  }

  return (
    <React.Fragment key={`${name}:${aligned.scope.generation}`}>
      {children}
    </React.Fragment>
  );
};

export default EventRouteGate;
